import { useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Button from "react-bootstrap/Button";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { DCUI_VERSION_PROPERTY_TYPE_ID } from "@constants/verification-service-details-constants";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import { ServicesValidationErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationServiceOffering,
    FormattedRpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { AssertionAttribute } from "@idw-types/data/reference-data/generic";
import { formatScoreModifiers } from "@formatters/rp-services/rp-service-configuration-score-formatter";
import { isAssertionScoreEnabled, isTrustScoreEnabled } from "@utils/scores/score-util";
import { getDefaultAttributeLabel } from "@utils/attribute-label-util";
import { hasDcuiSmsAndDcuiEmailServiceOffering } from "@utils/messaging-template/messaging-template-util";
import { setValueAndAddedToServiceForProperty } from "@utils/login-hint/login-hint-utils";
import { AddRpServiceConfigurationServiceOfferingModal } from "../../../modals/add-rp-service-configuration-service-offering-modal/add-rp-service-configuration-service-offering-modal";
import { HumanIdentityTabContainer } from "./human-identity-tab-container/human-identity-tab-container";
import { RelationshipsTabContainer } from "./relationships-tab-container/relationships-tab-container";
import { EnvironmentalTabContainer } from "./environmental-tab-container/environmental-tab-container";
import { ServicePropertiesTabContainer } from "./service-properties-tab-container/service-properties-tab-container";
import { RunOrderTabContainer } from "./run-order-tab-container/run-order-tab-container";
import { ThemingSubTabContainer } from "../../../common/theming-sub-tab-container/theming-sub-tab-container";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

type VerificationServiceDetailsServicesTabProps = {
    isDisabled: boolean;
    validationErrors: ServicesValidationErrors;
    assertionAttributes: Array<AssertionAttribute>;
    serviceOfferingRefreshRateDefault: string;
    assertionStatusMaps: Array<ApAssertionStatusMap>;
    assertionScoreServiceOfferingIDs: Array<string>;
    trustScoreServiceOfferingIDs: Array<string>;
    scoreServiceOfferingIDs: Array<string>;
    initialRpServiceConfiguration: FormattedRpServiceConfiguration;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    showIncodeFlowTabs: boolean;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const VerificationServiceDetailsServicesTab = (props: VerificationServiceDetailsServicesTabProps) => {
    const {
        isDisabled,
        validationErrors,
        assertionAttributes,
        serviceOfferingRefreshRateDefault,
        initialRpServiceConfiguration,
        rpServiceConfigurationState,
        assertionStatusMaps,
        assertionScoreServiceOfferingIDs,
        trustScoreServiceOfferingIDs,
        scoreServiceOfferingIDs,
        showIncodeFlowTabs,
        setRpServiceConfigurationState
    } = props;

    const modalContext = useModalContext();

    const addRpServiceConfigurationServiceOffering = (rpServiceConfigurationServiceOffering: FormattedRpServiceConfigurationServiceOffering) => {
        const newServiceOfferings = rpServiceConfigurationState.serviceOfferings.map(so => {
            if (so.uuid === rpServiceConfigurationServiceOffering.uuid) {
                let assertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion> = [];
                if (
                    rpServiceConfigurationServiceOffering.assertions.length > 0 &&
                    rpServiceConfigurationServiceOffering.assertions.filter(as => as.status === EntityStatusTypes.ACTIVE).length > 0
                ) {
                    assertions = rpServiceConfigurationServiceOffering.assertions;
                } else {
                    assertions = rpServiceConfigurationServiceOffering.assertions.map(assertion => ({
                        ...assertion,
                        status: EntityStatusTypes.ACTIVE,
                        addedToService: true
                    }));
                }

                return {
                    ...rpServiceConfigurationServiceOffering,
                    assertions: assertions,
                    fallbackStatus: "primary",
                    fallbackRPServiceSOID: null,
                    fallbackServiceOfferingID: null,
                    status: EntityStatusTypes.ACTIVE,
                    addedToService: true
                };
            } else {
                return so;
            }
        });

        const serviceOffering = newServiceOfferings.find(so => so.serviceOfferingID === rpServiceConfigurationServiceOffering.serviceOfferingID);

        const newAttributes: Array<FormattedRpServiceConfigurationAttribute> = [...rpServiceConfigurationState.attributes];
        if (serviceOffering) {
            const serviceOfferingAssertionIds = serviceOffering.assertions.map(assertion => assertion.assertionId);
            // get all new assertion attributes
            const newAssertionAttributes = cloneDeep(assertionAttributes.filter(aa => serviceOfferingAssertionIds.includes(aa.assertionID)));

            // create a list of the services current attribute ids
            const currentAttributes = rpServiceConfigurationState.attributes
                .filter(attribute => attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE);
            let maxOrder = 0;
            if (currentAttributes.length > 0) {
                maxOrder = Math.max(...currentAttributes.map(attr => attr.order)) + 1;
            }
            const currentAttributeIds = currentAttributes.map(attribute => attribute.id)

            // for each assertion attribute, add the attribute to the service if it doesn't already exist on it
            newAssertionAttributes.forEach(naa => {
                if (!currentAttributeIds.includes(naa.attributeTypeID)) {
                    const attribute = newAttributes.find(attr => attr.attributeType?.id === naa.attributeTypeID);
                    if (attribute) {
                        // enable the attribute
                        currentAttributeIds.push(attribute.attributeType?.id);
                        attribute.addedToService = true;
                        attribute.order = maxOrder;
                        attribute.status = EntityStatusTypes.ACTIVE;
                        // increment the max order
                        maxOrder += 1;

                        const attributeLabel = getDefaultAttributeLabel(
                            attribute.attributeType,
                            attribute.attributeLabels,
                            DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH]
                        );

                        if (attributeLabel) {
                            attribute.attributeLabel = attributeLabel;
                        }
                    }
                }
            });
        }

        let newProperties = rpServiceConfigurationState.properties;
        if (serviceOffering && hasDcuiSmsAndDcuiEmailServiceOffering([serviceOffering])) {
            newProperties = newProperties.map(property => {
                if (property.propertyTypeId === DCUI_VERSION_PROPERTY_TYPE_ID) {
                    return setValueAndAddedToServiceForProperty(property, "2.0", true);
                }
                return property;
            });
        }

        const newRpServiceConfigurationState = {
            ...rpServiceConfigurationState,
            serviceOfferings: newServiceOfferings,
            attributes: newAttributes,
            properties: newProperties
        };

        if (isAssertionScoreEnabled(newServiceOfferings, assertionScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.assertionScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.assertionScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }

        if (isTrustScoreEnabled(newServiceOfferings, trustScoreServiceOfferingIDs)) {
            newRpServiceConfigurationState.trustScoreModifiers = formatScoreModifiers(rpServiceConfigurationState.trustScoreModifiers, newServiceOfferings,
                assertionStatusMaps
            );
        }
        setRpServiceConfigurationState(newRpServiceConfigurationState);
    };

    const onClickAddService = (unselectedServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>, maxOrder: number) => {
        const renderAddServiceOfferingModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationServiceOfferingModal
                    rpServiceConfigurationServiceOfferings={unselectedServiceOfferings}
                    serviceOfferingRefreshRateDefault={serviceOfferingRefreshRateDefault}
                    addRpServiceConfigurationServiceOffering={addRpServiceConfigurationServiceOffering}
                    maxOrder={maxOrder}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddServiceOfferingModal,
            modalType: ModalTypes.AddServiceOffering
        });
    };

    const createHeaderButton = (serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>, maxOrder: number) => {
        if (isDisabled) {
            return null;
        } else {
            return (
                <Button id="add-service-offering" disabled={serviceOfferings.length === 0} onClick={() => onClickAddService(serviceOfferings, maxOrder)}>
                    {resources.pages.verificationServiceDetails.tabs.services.subTabs.common.buttons.addService.label}
                </Button>
            );
        }
    };

    const showRunOrderTab = useMemo(() => {
        return rpServiceConfigurationState.serviceOfferings.some(so =>
            ["HUMAN", "ENVIRONMENT", "RELATIONSHIP"].includes(so.category) &&
            so.addedToService &&
            so.status === EntityStatusTypes.ACTIVE &&
            so.fallbackStatus === "primary"
        );
    }, [rpServiceConfigurationState]);

    return (
        <ThemingSubTabContainer>
            <Tabs id="services" unmountOnExit={true}>
                <Tab eventKey={"services-human-identity"} title={validationErrors.humanIdentity.tabTitle}>
                    <HumanIdentityTabContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors.humanIdentity}
                        initialRpServiceConfiguration={initialRpServiceConfiguration}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        assertionStatusMaps={assertionStatusMaps}
                        assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                        trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                        scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                        createHeaderButton={createHeaderButton}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
                <Tab eventKey={"services-relationships"} title={validationErrors.relationships.tabTitle}>
                    <RelationshipsTabContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors.relationships}
                        initialRpServiceConfiguration={initialRpServiceConfiguration}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        assertionStatusMaps={assertionStatusMaps}
                        assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                        trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                        scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                        createHeaderButton={createHeaderButton}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
                <Tab eventKey={"services-environmental"} title={validationErrors.environmental.tabTitle}>
                    <EnvironmentalTabContainer
                        isDisabled={isDisabled}
                        validationErrors={validationErrors.environmental}
                        initialRpServiceConfiguration={initialRpServiceConfiguration}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        assertionStatusMaps={assertionStatusMaps}
                        assertionScoreServiceOfferingIDs={assertionScoreServiceOfferingIDs}
                        trustScoreServiceOfferingIDs={trustScoreServiceOfferingIDs}
                        scoreServiceOfferingIDs={scoreServiceOfferingIDs}
                        createHeaderButton={createHeaderButton}
                        setRpServiceConfigurationState={setRpServiceConfigurationState}
                    />
                </Tab>
                {showRunOrderTab && (
                    <Tab eventKey={"services-run-order"} title={resources.pages.verificationServiceDetails.tabs.services.subTabs.runOrder.tabName}>
                        <RunOrderTabContainer
                            isDisabled={isDisabled}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                        />
                    </Tab>
                )}
                {rpServiceConfigurationState.permissions.canViewRPServiceProperties &&
                    (<Tab eventKey={"services-properties"} title={resources.pages.verificationServiceDetails.tabs.services.subTabs.properties.tabName}>
                        <ServicePropertiesTabContainer
                            isDisabled={isDisabled}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            showIncodeFlowTabs={showIncodeFlowTabs}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                        />
                    </Tab>)}
            </Tabs>
        </ThemingSubTabContainer>
    );
};